const SOCIAL_PLATFORM_CONFIG = {
  instagramUrl: {
    baseUrl: 'https://instagram.com/',
    handlePrefix: '',
    supportsHandle: true,
  },
  facebookUrl: {
    baseUrl: 'https://facebook.com/',
    handlePrefix: '',
    supportsHandle: true,
  },
  twitterUrl: {
    baseUrl: 'https://x.com/',
    handlePrefix: '',
    supportsHandle: true,
  },
  pinterestUrl: {
    baseUrl: 'https://pinterest.com/',
    handlePrefix: '',
    supportsHandle: true,
  },
  appleMusicUrl: {
    baseUrl: 'https://',
    supportsHandle: false,
  },
  mixcloudUrl: {
    baseUrl: 'https://mixcloud.com/',
    handlePrefix: '',
    supportsHandle: true,
  },
  tiktokUrl: {
    baseUrl: 'https://tiktok.com/',
    handlePrefix: '@',
    supportsHandle: true,
  },
  soundcloudUrl: {
    baseUrl: 'https://soundcloud.com/',
    handlePrefix: '',
    supportsHandle: true,
  },
  spotifyUrl: {
    baseUrl: 'https://',
    supportsHandle: false,
  },
  youtubeUrl: {
    baseUrl: 'https://youtube.com/',
    handlePrefix: '@',
    supportsHandle: true,
  },
};

export const SOCIAL_LINK_FIELDS = Object.freeze([
  'instagramUrl',
  'facebookUrl',
  'twitterUrl',
  'pinterestUrl',
  'appleMusicUrl',
  'mixcloudUrl',
  'tiktokUrl',
  'soundcloudUrl',
  'spotifyUrl',
  'youtubeUrl',
]);

export const DIRECT_CONTACT_FIELDS = Object.freeze([
  'contactEmail',
  'contactPhone',
  'preferredContactDetails',
]);

export const DIRECT_SOCIAL_CONTACT_FIELDS = Object.freeze([
  'instagramUrl',
  'facebookUrl',
  'tiktokUrl',
]);

export const PREFERRED_CONTACT_METHOD_OPTIONS = Object.freeze([
  { value: '', label: 'No preference' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone / WhatsApp' },
  { value: 'instagram', label: 'Instagram DM' },
  { value: 'facebook', label: 'Facebook DM' },
  { value: 'tiktok', label: 'TikTok DM' },
  { value: 'other', label: 'Other' },
]);

function stripHandlePrefix(value = '') {
  return String(value || '').trim().replace(/^@+/, '');
}

function hasScheme(value = '') {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(String(value || '').trim());
}

function looksLikeBareHandle(value = '') {
  const normalized = stripHandlePrefix(value);
  return !!normalized && !/[.\s/]/.test(normalized);
}

function startsWithPlatformDomain(baseUrl = '', value = '') {
  const domain = String(baseUrl || '')
    .replace(/^https?:\/\//i, '')
    .toLowerCase();
  return !!domain && String(value || '').toLowerCase().startsWith(domain);
}

export function normalizeSocialLinkValue(field, value) {
  const normalized = String(value || '').trim();
  if (!normalized) return '';

  const config = SOCIAL_PLATFORM_CONFIG[field];
  if (!config) return normalized;

  if (hasScheme(normalized)) {
    return normalized;
  }

  if (normalized.startsWith('@') && config.supportsHandle) {
    const handle = stripHandlePrefix(normalized);
    return `${config.baseUrl}${config.handlePrefix || ''}${handle}`;
  }

  if (config.supportsHandle && looksLikeBareHandle(normalized)) {
    const handle = stripHandlePrefix(normalized);
    return `${config.baseUrl}${config.handlePrefix || ''}${handle}`;
  }

  const sanitizedValue = normalized.replace(/^\/+/, '');
  if (/^[a-z0-9.-]+\.[a-z]{2,}(?:\/|$)/i.test(sanitizedValue)) {
    return startsWithPlatformDomain(config.baseUrl, sanitizedValue)
      ? `https://${sanitizedValue}`
      : `${config.baseUrl === 'https://' ? 'https://' : config.baseUrl}${sanitizedValue}`;
  }

  if (normalized.startsWith('/')) {
    return `${config.baseUrl === 'https://' ? 'https://' : config.baseUrl}${sanitizedValue}`;
  }

  return normalized;
}

export function normalizePreferredContactMethod(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return PREFERRED_CONTACT_METHOD_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : '';
}

export function normalizeGigProfileFormValues(form = {}) {
  const normalized = { ...form };
  SOCIAL_LINK_FIELDS.forEach((field) => {
    normalized[field] = normalizeSocialLinkValue(field, form?.[field]);
  });
  normalized.contactEmail = String(form?.contactEmail || '').trim();
  normalized.contactPhone = String(form?.contactPhone || '').trim();
  normalized.preferredContactMethod = normalizePreferredContactMethod(form?.preferredContactMethod);
  normalized.preferredContactDetails = String(form?.preferredContactDetails || '').trim();
  normalized.storeProfile = form?.storeProfile !== false;
  normalized.allowContactSharing = !!form?.allowContactSharing;
  return normalized;
}

export function hasAtLeastOneSocialLink(form = {}) {
  const normalized = normalizeGigProfileFormValues(form);
  return SOCIAL_LINK_FIELDS.some((field) => !!normalized[field]);
}

export function hasAtLeastOneCommunicationMethod(form = {}) {
  const normalized = normalizeGigProfileFormValues(form);
  return (
    DIRECT_CONTACT_FIELDS.some((field) => !!normalized[field]) ||
    DIRECT_SOCIAL_CONTACT_FIELDS.some((field) => !!normalized[field])
  );
}
